// Analytics tab › Reports: master-detail — the project's formatted (Jasper)
// reports (R) on the left, the EXISTING embedded ReportDesignerPage on the
// right. "New" drafts the report + its JRXML with AI, or creates a blank one
// on the default A4 template. Reports publish to the app menu like dashboards.
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, Circle, ListPlus } from 'lucide-react'
import ReportDesignerPage from '@/pages/designers/ReportDesignerPage'
import {
  generateReportObject,
  listProjectReports,
  objectCatalog,
  saveReportObject,
  type CatalogObject,
  type ProjectReportSummary,
} from '@/api/designers'
import { blankReportModel, defaultReportJrxml } from '@/pages/designers/report/types'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import PublishToMenuDialog, { type PublishTarget } from './PublishToMenuDialog'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

export default function ReportsSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [reports, setReports] = useState<ProjectReportSummary[]>([])
  const [catalog, setCatalog] = useState<Record<string, CatalogObject>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuTarget, setMenuTarget] = useState<PublishTarget | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The list gives rich meta; the catalog adds used/unused. The catalog is
      // best-effort — the list alone is enough to render.
      const [listRes, catRes] = await Promise.allSettled([
        listProjectReports(project),
        objectCatalog(project, 'R'),
      ])
      if (listRes.status === 'rejected') throw listRes.reason
      setReports(listRes.value.reports || [])
      if (catRes.status === 'fulfilled') {
        const map: Record<string, CatalogObject> = {}
        for (const o of catRes.value.objects || []) map[o.obj_name] = o
        setCatalog(map)
      }
    } catch (err) {
      setError(errMessage(err, 'Could not load reports'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = reports.map((r) => {
    const cat = catalog[r.obj_name]
    const hasTitle = !!r.title && r.title !== r.obj_name
    return {
      id: r.obj_name,
      name: r.title || r.obj_name,
      subtitle: hasTitle ? r.obj_name : undefined,
      description: r.description || undefined,
      tags: r.default_format ? [`${r.default_format.toUpperCase()} by default`] : undefined,
      badge: cat
        ? (cat.used
            ? { text: 'On the app menu / in use', tone: 'success' as const, icon: <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} /> }
            : { text: 'Not used yet', tone: 'muted' as const, icon: <Circle size={14} style={{ color: 'var(--color-text-faint)' }} /> })
        : undefined,
    }
  })

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await generateReportObject({
        project,
        obj_name: objName,
        description: description || name,
      })
      toast.success(`Report "${name}" drafted — open it to refine the layout`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the report'))
      return null
    }
  }

  // Manual path: persist a blank report on the default A4 JRXML template so the
  // user designs the bands themselves.
  const handleCreateManual = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      const model = blankReportModel(objName)
      model.TITLE = name.trim() || objName
      if (description.trim()) model.DESCRIPTION = description.trim()
      await saveReportObject(project, objName, {
        model,
        jrxml_files: { [model.REPORT.MAIN_JRXML]: defaultReportJrxml(objName) },
      })
      toast.success(`Report "${name}" created — design its layout`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the report'))
      return null
    }
  }

  return (
    <>
      <ObjectMasterDetail
        project={project}
        typeCode="R"
        heading="Reports"
        subCopy="Pixel-perfect printable reports (Jasper/JRXML) — invoices, statements, listings. They run against the same data sources as your visuals and export to PDF, Excel, HTML, Word or CSV."
        items={items}
        loading={loading}
        error={error}
        onRetry={() => void refresh()}
        newLabel="New report"
        newDialogTitle="New report"
        namePlaceholder="e.g. Customer Statement"
        descPlaceholder="e.g. A monthly statement per customer: opening balance, invoices and payments in the period, closing balance. A4 portrait."
        createButtonLabel="Generate with AI"
        onCreate={handleCreate}
        onCreateManual={handleCreateManual}
        manualButtonLabel="Create manually"
        onObjectSaved={() => { void refresh(); onChanged?.() }}
        registerLeaveGuard={registerLeaveGuard}
        initialObjName={initialObjName}
        openNonce={openNonce}
        detailToolbar={(objName) => {
          const cat = catalog[objName]
          if (!cat || cat.in_navigation) return null
          const r = reports.find((x) => x.obj_name === objName)
          return (
            <button
              className="btn btn-secondary btn-sm"
              title="Wire this report into the application menu — no regeneration"
              onClick={() => setMenuTarget({
                obj_name: objName,
                obj_type: 'R',
                title: r?.title || cat.title || objName,
              })}
            >
              <ListPlus size={13} />
              Add to menu…
            </button>
          )
        }}
        emptyState={(openNew) => (
          <EmptyStateCoach
            icon={<TypeChip code="R" size={26} />}
            title="No reports yet"
            body="A report is a fixed-layout document your users print or download — an invoice, a statement, a listing. Draft one from a description with AI, or start from a blank A4 page."
            action={
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                + New report
              </button>
            }
          />
        )}
        renderDesigner={(objName, common) => (
          <ReportDesignerPage key={`${project}:${objName}`} {...common} />
        )}
      />
      <PublishToMenuDialog
        open={menuTarget !== null}
        mode="menu"
        project={project}
        targets={menuTarget ? [menuTarget] : []}
        onClose={() => setMenuTarget(null)}
        onPublished={() => { void refresh(); onChanged?.() }}
      />
    </>
  )
}
